import React, { useState, useRef, useEffect } from 'react';
import { Check } from 'lucide-react';
import { demofile, demofileSiblingPoster } from '../utils/paths';
import { useLanguage } from '../contexts/LanguageContext';
import { getT } from '../utils/translations';
import { trackEvent } from '../utils/gtag';

const TAB_IDS = ['gen'] as const;
type TabId = typeof TAB_IDS[number];

const TAB_VIDEOS: Record<TabId, string> = {
  gen: demofile(`轮播demo/${encodeURIComponent('10261.mp4')}`),
} as any;

const TAB_POSTERS: Record<TabId, string> = {
  gen: demofileSiblingPoster('轮播demo/10261.mp4'),
} as any;

interface ProductCapabilitiesProps {
  onNavigateToExperience?: (tabId: string) => void;
  onNavigateToIntro?: (tabId: string) => void;
}

export default function ProductCapabilities({ onNavigateToExperience, onNavigateToIntro }: ProductCapabilitiesProps) {
  const { lang } = useLanguage();
  const t = getT(lang);

  const [activeTabId, setActiveTabId] = useState<TabId>('gen');
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaReady, setMediaReady] = useState(false);
  const activeTab = t.product.tabs[activeTabId];

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || mediaReady) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setMediaReady(true);
          io.disconnect();
        }
      },
      { rootMargin: '250px 0px', threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [mediaReady]);

  useEffect(() => {
    if (mediaReady && videoRef.current) {
      videoRef.current.currentTime = 0;
      const p = videoRef.current.play();
      if (p !== undefined) {
        p.catch(() => {
          if (videoRef.current) {
            videoRef.current.muted = true;
            videoRef.current.play().catch(() => {});
          }
        });
      }
    }
  }, [activeTabId, mediaReady]);

  return (
    <section className="py-8 sm:py-12 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 text-gray-900 tracking-tight">
            {t.product.sectionTitle}
          </h2>
        </div>

        {/* Tabs (hide when only one) */}
        {TAB_IDS.length > 1 && (
          <div className="flex flex-wrap justify-center gap-x-8 sm:gap-x-20 gap-y-3 sm:gap-y-4 mb-8 sm:mb-12">
            {TAB_IDS.map((id) => (
              <button
                key={id}
                onClick={() => setActiveTabId(id)}
                className={`text-xl sm:text-2xl transition-all duration-300 pb-3 border-b-2 ${
                  activeTabId === id
                    ? 'text-primary border-primary font-semibold'
                    : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-200'
                }`}
              >
                {t.product.tabs[id].label}
              </button>
            ))}
          </div>
        )}

        {/* Content Card — stacks vertically on mobile */}
        <div ref={sectionRef} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden flex flex-col lg:flex-row lg:h-[520px]">

          {/* Left: Text */}
          <div className="w-full lg:w-5/12 p-6 sm:p-8 lg:p-12 flex flex-col justify-center">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-gray-900">{activeTab.title}</h3>
            <ul className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              {activeTab.features.map((feat, i) => (
                <li key={i} className="flex items-start">
                  <div className="mt-1 mr-3 text-primary">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                  </div>
                  <span className="text-gray-700 text-base sm:text-lg leading-relaxed">{feat}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <button
                onClick={() => {
                  trackEvent('click_learn_more');
                  // 固定到生成的介绍页
                  window.location.hash = 'learn-gen';
                  onNavigateToIntro?.('gen');
                }}
                className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-5 sm:px-8 py-2.5 sm:py-3.5 rounded-lg text-base sm:text-lg font-medium transition-colors"
              >
                {t.product.learnMore}
              </button>

              <button
                onClick={() => { trackEvent('click_try_now'); onNavigateToExperience?.('gen_tryNow'); window.location.hash = 'try-gen'; }}
                className="bg-gradient-to-r from-primary to-blue-500 hover:to-primary-hover text-white px-5 sm:px-8 py-2.5 sm:py-3.5 rounded-lg text-base sm:text-lg font-medium transition-colors shadow-sm hover:shadow-md"
              >
                {t.product.tryNow}
              </button>
            </div>
          </div>

          {/* Right: Video */}
          <div className="w-full lg:w-7/12 bg-white relative overflow-hidden flex items-center justify-center p-4 sm:p-8 lg:p-12 min-h-[220px] sm:min-h-[280px]">
            <div className="relative w-full max-w-2xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black">
              <video
                ref={videoRef}
                key={activeTabId}
                src={mediaReady ? TAB_VIDEOS[activeTabId] : undefined}
                poster={TAB_POSTERS[activeTabId]}
                className="w-full h-full object-contain"
                muted
                loop
                autoPlay={mediaReady}
                playsInline
                preload={mediaReady ? 'metadata' : 'none'}
                fetchPriority={mediaReady && activeTabId === 'gen' ? 'high' : 'auto'}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
